import { useState } from 'react'

import { OrderContainer } from '~/components/dashboard/clients/timeline/prescriptions/rxTracker/OrderContainer'
import { PrescriptionType } from '~/constants/prescription'
import type { RxCardItem } from '~/types'

import { AdministrationInstructionsSection } from './AdministrationInstructionsSection'
import type { OrderTableRow } from './components/OrderTable'
import { OrderTable } from './components/OrderTable'
import { LabelValueWrapper } from './LabelValueWrapper'
import { getPrescriptionFromRow } from './rxCardUtilities'
import RxOrderTableMenu from './RxOrderTableMenu'
import { useDispenseRefill } from './useDispenseRefill'
import { useRxCardContent } from './useRxCardContent'
import { useRxOrderTableShipments } from './useRxOrderTableShipments'

import styles from './RxCardDetails.module.scss'

export interface RxCardDetailsProps {
  index?: number
  isDetailView?: boolean
  isInvoice?: boolean
  item: RxCardItem
}

export const RxCardDetails = ({
  item,
  index,
  isDetailView = false,
  isInvoice,
}: RxCardDetailsProps) => {
  const [isOrderTableExpanded, setIsOrderTableExpanded] = useState(false)

  const { prescription } = item
  const itemDate =
    item.date ??
    item.prescription?.updatedAt ??
    item.prescription?.creationDate ??
    ''
  const handleDispenseRefill = useDispenseRefill(item)
  const isChewy = prescription?.prescriptionType === PrescriptionType.CHEWY
  const { data: rxUsagesData, orderTableLoadedRx } = useRxOrderTableShipments(
    prescription,
    isChewy,
    isOrderTableExpanded,
  )
  const rxContent = useRxCardContent(item, {
    itemDate,
    onDispenseRefill: handleDispenseRefill,
    prescriptionForQtyAndShipping: orderTableLoadedRx ?? undefined,
    rxUsagesData: isChewy ? (rxUsagesData ?? null) : undefined,
  })

  return (
    <div className={styles.container}>
      <div className={styles.fieldsGrid}>
        {rxContent.fields.map((field) => (
          <LabelValueWrapper
            boldValue={field.boldValue}
            key={field.label}
            label={field.label}
            secondaryText={field.secondaryText}
            tooltipText={field.tooltipText}
            useSecondaryTextStyle={field.useSecondaryTextStyle}
            value={field.value}
            onClick={field.onClick}
          />
        ))}
      </div>
      {rxContent.showTime && (
        <div className={styles.timeDisplay}>{rxContent.showTime}</div>
      )}
      {isDetailView && (
        <>
          <div className={styles.divider} />
          <AdministrationInstructionsSection prescription={prescription} />
          <div className={styles.divider} />
          <div className={styles.instructionsContainer}>
            <LabelValueWrapper
              label={rxContent.instructions.label}
              value={rxContent.instructions.value ?? ''}
            />
          </div>
          <div className={styles.divider} />
        </>
      )}
      {!isDetailView &&
        !rxContent.isCustomView &&
        (prescription?.notes ?? '').trim() && (
          <>
            <div className={styles.divider} />
            <div className={styles.instructionsContainer}>
              <LabelValueWrapper
                label={rxContent.instructions.label}
                value={rxContent.instructions.value}
              />
            </div>
          </>
        )}
      {rxContent.orderTableData && !rxContent.isCustomView && (
        <div
          className={
            isDetailView
              ? styles.orderContainerWrapper
              : styles.orderContainerWrapperWithSpacing
          }
        >
          <OrderContainer
            expanded={isOrderTableExpanded}
            title={isDetailView ? undefined : (rxContent.orderTableTitle ?? '')}
            onChange={(_event, expanded: boolean) =>
              setIsOrderTableExpanded(expanded)
            }
          >
            <OrderTable
              cardHeaders={rxContent.orderTableData.cardHeaders}
              className={
                isDetailView
                  ? styles.orderTableDetailView
                  : styles.orderTableTimeline
              }
              headers={rxContent.orderTableData.headers}
              renderRowActions={(row: OrderTableRow[]) => (
                <RxOrderTableMenu
                  index={index}
                  isInvoice={isInvoice}
                  item={item}
                  refill={getPrescriptionFromRow(row, true)}
                />
              )}
              rows={rxContent.orderTableData.rows}
              subHeader={rxContent.orderTableSubHeader}
            />
          </OrderContainer>
        </div>
      )}
    </div>
  )
}
